// ═══════════════════════════════════════════════════════════════
// Panneau Entretiens (HITL Scheduling Sabiloo + relationnel Lot 1C-0/1C-1).
// ═══════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Check, RefreshCw, X, Clock, User, Link2, Plus, Trash2 } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import * as bookingApi from '../../services/bookingApi';

const palette = {
  primary: '#5B4FE5',
  primarySoft: 'rgba(91,79,229,0.12)',
  primaryBorder: 'rgba(91,79,229,0.28)',
  ink: 'var(--text)',
  muted: 'var(--text-secondary)',
  cardBg: 'var(--card-bg)',
  cardBorder: 'var(--card-border)',
  bg: 'var(--bg)',
  green: '#0F7A4A',
  greenSoft: 'rgba(15,122,74,0.12)',
  orange: '#B07814',
  orangeSoft: 'rgba(176,120,20,0.12)',
  red: '#C7493F',
  redSoft: 'rgba(199,73,63,0.10)',
};

const WEEKDAYS = [
  { v: 1, label: 'Lun', full: 'Lundi' },
  { v: 2, label: 'Mar', full: 'Mardi' },
  { v: 3, label: 'Mer', full: 'Mercredi' },
  { v: 4, label: 'Jeu', full: 'Jeudi' },
  { v: 5, label: 'Ven', full: 'Vendredi' },
  { v: 6, label: 'Sam', full: 'Samedi' },
  { v: 7, label: 'Dim', full: 'Dimanche' },
];

const DEFAULT_RANGE = { start_time: '09:00', end_time: '17:00' };

function normalizeTimeValue(value) {
  return String(value || '').slice(0, 5);
}

function rulesForWeekday(rules, weekday) {
  return rules
    .filter((r) => Number(r.weekday) === weekday)
    .map((r) => ({
      start_time: normalizeTimeValue(r.start_time),
      end_time: normalizeTimeValue(r.end_time),
    }))
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
}

function parseMinutes(t) {
  const [h, m] = normalizeTimeValue(t).split(':').map(Number);
  return h * 60 + m;
}

function validateAvailabilityDraft(rules) {
  const byDay = new Map();
  for (const r of rules) {
    const wd = Number(r.weekday);
    const start = parseMinutes(r.start_time);
    const end = parseMinutes(r.end_time);
    if (end <= start) {
      const day = WEEKDAYS.find((d) => d.v === wd);
      return `Heure de fin doit être après le début (${day?.full || wd}).`;
    }
    if (!byDay.has(wd)) byDay.set(wd, []);
    byDay.get(wd).push({
      start,
      end,
      start_time: normalizeTimeValue(r.start_time),
      end_time: normalizeTimeValue(r.end_time),
      weekday: wd,
    });
  }
  for (const [, windows] of byDay) {
    windows.sort((a, b) => a.start - b.start);
    for (let i = 1; i < windows.length; i += 1) {
      if (windows[i].start < windows[i - 1].end) {
        const day = WEEKDAYS.find((d) => d.v === windows[i].weekday);
        return `Plages chevauchantes le ${day?.full || windows[i].weekday} : ${windows[i - 1].start_time}–${windows[i - 1].end_time} et ${windows[i].start_time}–${windows[i].end_time}`;
      }
    }
  }
  return null;
}

function serializeRules(rules) {
  return rules
    .map((r) => ({
      weekday: Number(r.weekday),
      start_time: normalizeTimeValue(r.start_time),
      end_time: normalizeTimeValue(r.end_time),
    }))
    .sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time));
}

const STATUS_META = {
  requested: { label: 'À confirmer', color: palette.orange, bg: palette.orangeSoft },
  confirmed: { label: 'Confirmé', color: palette.green, bg: palette.greenSoft },
  declined: { label: 'Refusé', color: palette.red, bg: palette.redSoft },
  cancelled: { label: 'Annulé', color: palette.muted, bg: 'rgba(128,128,128,0.1)' },
  done: { label: 'Terminé', color: palette.primary, bg: palette.primarySoft },
};

/** Lot 1C-0 — libellés statut RDV dans l’historique relationnel. */
const APPOINTMENT_HISTORY_LABELS = {
  requested: 'RDV à confirmer',
  confirmed: 'RDV confirmé',
  done: 'RDV terminé',
  declined: 'RDV refusé',
  cancelled: 'RDV annulé',
};

function formatRelationDate(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRelationDateShort(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function buildPublicBookingUrl(hostSlug) {
  if (!hostSlug) return null;
  const apiBase =
    process.env.REACT_APP_BOOKING_PUBLIC_API ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const widgetBase = process.env.REACT_APP_SABILOO_BOOKING_URL || 'https://sabiloo.com/prendre-rdv/';
  const sep = widgetBase.includes('?') ? '&' : '?';
  return `${widgetBase}${sep}api=${encodeURIComponent(apiBase)}&slug=${encodeURIComponent(hostSlug)}`;
}

/**
 * Lot 1C-2 — lien de préparation : ne contient JAMAIS de donnée personnelle ou
 * interne (pas de nom, email, téléphone, contact_id, relation_id, proposal_id).
 * Uniquement slug host + offering + créneau (mêmes champs publics que le widget).
 */
function buildPrepareDeepLink(hostSlug, offeringCode, slotIso) {
  const base = buildPublicBookingUrl(hostSlug);
  if (!base || !offeringCode || !slotIso) return null;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}offering=${encodeURIComponent(offeringCode)}&slot=${encodeURIComponent(slotIso)}`;
}

/** Offering à préparer : celui du RDV déclencheur (proposal) si toujours actif, sinon offering actif par défaut. */
function resolvePreparationOffering(proposal, appointments, offerings) {
  const triggerAppt = (appointments || []).find(
    (a) => Number(a.id) === Number(proposal?.trigger_appointment_id)
  );
  const triggerCode = triggerAppt?.offering_code || null;
  const triggerActive = triggerCode
    ? (offerings || []).find((o) => o.code === triggerCode && o.is_active)
    : null;
  if (triggerActive) {
    return { code: triggerActive.code, title: triggerActive.title };
  }
  const fallback = (offerings || []).find((o) => o.is_active);
  return fallback ? { code: fallback.code, title: fallback.title } : null;
}

function formatSlotLabel(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Lot 1C-2 — préparer un créneau à proposer. Aucune écriture DB, aucun event CRM :
 * une simple sélection en mémoire React. Reload = préparation perdue (volontaire V1).
 * Les créneaux proviennent exclusivement de BookingManager.getAvailability (Scheduling).
 */
function PrepareNextMeetingPicker({ contact, hostSlug, offering }) {
  const [slots, setSlots] = useState(null); // null = chargement
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSelected(null);
    if (!offering?.code) {
      setSlots([]);
      return undefined;
    }
    setSlots(null);
    setError(null);
    bookingApi
      .getMyAvailability(offering.code)
      .then((data) => {
        if (cancelled) return;
        setSlots((data.slots || []).slice(0, 5));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Disponibilités indisponibles');
        setSlots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [offering?.code]);

  if (!offering) {
    return (
      <div style={{ fontSize: 12, color: palette.muted }}>
        Aucun type d’entretien actif — configurez une offre dans « Dispos & profil » pour préparer
        un créneau.
      </div>
    );
  }

  const copyLink = async () => {
    const link = buildPrepareDeepLink(hostSlug, offering.code, selected.starts_at);
    if (!link) {
      toast.error('Lien indisponible');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Lien copié — aucun rendez-vous créé');
    } catch {
      toast.info(link);
    }
  };

  if (selected) {
    return (
      <div
        style={{
          marginTop: 10,
          padding: '10px 12px',
          borderRadius: 8,
          border: `1px solid ${palette.cardBorder}`,
          background: palette.cardBg,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: palette.muted, marginBottom: 6 }}>
          Créneau préparé
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: palette.ink }}>{contact?.name}</div>
        <div style={{ fontSize: 12, color: palette.ink, marginTop: 2 }}>{offering.title}</div>
        <div style={{ fontSize: 12, color: palette.primary, fontWeight: 600, marginTop: 2 }}>
          {formatSlotLabel(selected.starts_at)}
        </div>
        <div style={{ fontSize: 11, color: palette.muted, marginTop: 8 }}>
          Aucun rendez-vous n’a été créé.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={copyLink}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${palette.primaryBorder}`,
              background: palette.primarySoft,
              color: palette.primary,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Link2 size={14} /> Copier le lien à envoyer
          </button>
          <button
            type="button"
            onClick={() => setSelected(null)}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${palette.cardBorder}`,
              background: 'transparent',
              color: palette.muted,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Changer de créneau
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: palette.muted, marginBottom: 6 }}>
        Préparer le prochain échange
      </div>
      <div style={{ fontSize: 12, color: palette.ink, marginBottom: 8 }}>
        {contact?.name} · {offering.title}
      </div>
      {slots === null ? (
        <div style={{ fontSize: 12, color: palette.muted }}>Recherche des prochains créneaux…</div>
      ) : error ? (
        <div style={{ fontSize: 12, color: palette.red }}>{error}</div>
      ) : slots.length === 0 ? (
        <div style={{ fontSize: 12, color: palette.muted }}>
          Aucun créneau disponible actuellement.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {slots.map((s) => (
            <button
              key={s.starts_at}
              type="button"
              onClick={() => setSelected(s)}
              style={{
                textAlign: 'left',
                padding: '8px 12px',
                borderRadius: 8,
                border: `1px solid ${palette.cardBorder}`,
                background: palette.cardBg,
                color: palette.ink,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ○ {formatSlotLabel(s.starts_at)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RelationFollowUpBanner({
  proposal,
  relationId,
  hostSlug,
  onDecided,
  contact,
  appointments,
  offerings,
}) {
  const [busy, setBusy] = useState(false);

  if (!proposal) return null;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await bookingApi.decideRelationProposal(relationId, proposal.id, decision);
      toast.success(
        decision === 'accept'
          ? 'Suite à préparer — aucun rendez-vous créé'
          : decision === 'defer'
            ? 'Proposition reportée'
            : 'Proposition écartée'
      );
      onDecided?.();
    } catch (err) {
      toast.error(err.message || 'Décision impossible');
    } finally {
      setBusy(false);
    }
  };

  if (proposal.status === 'accepted') {
    return (
      <div
        style={{
          marginTop: 12,
          padding: '12px 14px',
          borderRadius: 10,
          border: `1px solid ${palette.green}`,
          background: palette.greenSoft,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: palette.ink, marginBottom: 6 }}>
          {proposal.title}
        </div>
        <div style={{ fontSize: 13, color: palette.ink, marginBottom: 4 }}>{proposal.message}</div>
        <div style={{ fontSize: 12, color: palette.muted }}>{proposal.submessage}</div>
        <PrepareNextMeetingPicker
          contact={contact}
          hostSlug={hostSlug}
          offering={resolvePreparationOffering(proposal, appointments, offerings)}
        />
      </div>
    );
  }

  if (proposal.status !== 'proposed') return null;

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 10,
        border: `1px solid ${palette.primaryBorder}`,
        background: palette.cardBg,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: palette.ink, marginBottom: 6 }}>
        {proposal.title}
      </div>
      <div style={{ fontSize: 13, color: palette.ink, marginBottom: 4 }}>{proposal.message}</div>
      <div style={{ fontSize: 13, color: palette.muted, marginBottom: 12 }}>{proposal.question}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => decide('accept')}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1px solid ${palette.primaryBorder}`,
            background: palette.primarySoft,
            color: palette.primary,
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Préparer la suite
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => decide('defer')}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1px solid ${palette.cardBorder}`,
            background: 'transparent',
            color: palette.ink,
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Plus tard
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => decide('dismiss')}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1px solid ${palette.cardBorder}`,
            background: 'transparent',
            color: palette.muted,
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Écarter
        </button>
      </div>
    </div>
  );
}

function RelationContextCard({ appointment, hostSlug, onRelationUpdated, offerings }) {
  const { relation } = appointment;

  if (!relation) {
    const isNonRetained =
      appointment.status === 'declined' ||
      (appointment.status === 'cancelled' && !appointment.confirmed_at);
    const potentialLabel = isNonRetained ? 'Relation non retenue' : 'Relation à qualifier';
    return (
      <div
        style={{
          marginTop: 14,
          padding: '12px 14px',
          borderRadius: 12,
          border: `1px solid ${palette.cardBorder}`,
          background: palette.bg,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: palette.ink, marginBottom: 6 }}>
          Relation potentielle
        </div>
        <div style={{ fontSize: 13, color: palette.muted, marginBottom: 8 }}>{potentialLabel}</div>
        {appointment.contact_activity ? (
          <div style={{ fontSize: 12, color: palette.ink }}>
            <span style={{ color: palette.muted, fontWeight: 600 }}>Activité · </span>
            {appointment.contact_activity}
          </div>
        ) : null}
        {appointment.motif ? (
          <div style={{ fontSize: 12, color: palette.ink, marginTop: 4 }}>
            <span style={{ color: palette.muted, fontWeight: 600 }}>Motif · </span>
            {appointment.motif}
          </div>
        ) : null}
        <p style={{ margin: '10px 0 0', fontSize: 11, lineHeight: 1.45, color: palette.muted }}>
          Aucune relation durable tant que le rendez-vous n’est pas confirmé. L’humain reste
          décisionnaire.
        </p>
      </div>
    );
  }

  const isNewRequest = appointment.status === 'requested';
  const isDeclinedRequest = appointment.status === 'declined';
  const isCancelledBeforeConfirm =
    appointment.status === 'cancelled' && !appointment.confirmed_at;

  const lastEvent = relation.events.length
    ? relation.events[relation.events.length - 1]
    : null;
  const timelineDesc = [...relation.events].reverse();

  return (
    <div
      style={{
        marginTop: 14,
        padding: '12px 14px',
        borderRadius: 12,
        border: `1px solid ${palette.primaryBorder}`,
        background: palette.primarySoft,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: palette.ink }}>
            {relation.contact.name}
          </div>
          <div style={{ fontSize: 12, color: palette.primary, fontWeight: 600, marginTop: 2 }}>
            {relation.status_label || relation.status}
          </div>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: palette.muted,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 999,
            padding: '3px 8px',
            background: palette.cardBg,
          }}
        >
          Contexte relationnel
        </span>
      </div>

      {isNewRequest ? (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: palette.orange,
            marginBottom: 10,
          }}
        >
          Nouvelle demande à qualifier
        </div>
      ) : null}
      {isDeclinedRequest ? (
        <div style={{ fontSize: 12, color: palette.muted, marginBottom: 10 }}>
          Demande refusée — la relation existante reste visible ci-dessous.
        </div>
      ) : null}
      {isCancelledBeforeConfirm ? (
        <div style={{ fontSize: 12, color: palette.muted, marginBottom: 10 }}>
          Demande annulée avant confirmation — la relation existante reste visible ci-dessous.
        </div>
      ) : null}
      {lastEvent ? (
        <div style={{ fontSize: 12, color: palette.ink, marginBottom: 12 }}>
          <span style={{ color: palette.muted, fontWeight: 600 }}>Dernier événement · </span>
          {lastEvent.label}
          <span style={{ color: palette.muted }}> · {formatRelationDateShort(lastEvent.created_at)}</span>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: palette.muted, marginBottom: 12 }}>
          Timeline en cours de constitution.
        </div>
      )}

      {relation.appointments.length > 0 ? (
        <div style={{ marginBottom: timelineDesc.length ? 12 : 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: palette.muted,
              marginBottom: 6,
            }}
          >
            Historique rendez-vous
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: palette.ink }}>
            {relation.appointments.map((ap) => (
              <li
                key={ap.id}
                style={{
                  marginBottom: 4,
                  fontWeight: ap.is_current ? 700 : 400,
                  color: ap.is_current ? palette.primary : palette.ink,
                }}
              >
                {formatRelationDateShort(ap.starts_at)} —{' '}
                {APPOINTMENT_HISTORY_LABELS[ap.status] || ap.status}
                {ap.offering_title ? ` · ${ap.offering_title}` : ''}
                {ap.is_current ? ' (ce RDV)' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {timelineDesc.length > 0 ? (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: palette.muted,
              marginBottom: 6,
            }}
          >
            Timeline
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: palette.ink }}>
            {timelineDesc.map((ev) => (
              <li key={ev.id} style={{ marginBottom: 4 }}>
                {formatRelationDateShort(ev.created_at)} — {ev.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <RelationFollowUpBanner
        proposal={relation.active_proposal}
        relationId={relation.id}
        hostSlug={hostSlug}
        onDecided={onRelationUpdated}
        contact={relation.contact}
        appointments={relation.appointments}
        offerings={offerings}
      />

      <p style={{ margin: '10px 0 0', fontSize: 11, lineHeight: 1.45, color: palette.muted }}>
        Le système restitue le contexte relationnel. L’humain reste décisionnaire.
      </p>
    </div>
  );
}

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function BookingPage() {
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [rules, setRules] = useState([]);
  const [rulesDraft, setRulesDraft] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('requested');
  const [tab, setTab] = useState('inbox');
  const [slugDraft, setSlugDraft] = useState('');
  const [displayDraft, setDisplayDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bookingApi.getMyHost();
      setHost(data.host);
      setOfferings(data.offerings || []);
      setRules(data.availability_rules || []);
      setRulesDraft(data.availability_rules || []);
      setSlugDraft(data.host?.slug || '');
      setDisplayDraft(data.host?.display_name || '');
      const ap = await bookingApi.listAppointments(filter || undefined);
      setAppointments(ap.appointments || []);
    } catch (err) {
      toast.error(err.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const onConfirm = async (id) => {
    const ok = await confirmModal(
      'Confirmer ce créneau ? Un email pourra être envoyé au prospect (si Listmonk est configuré).',
      { confirmText: 'Confirmer le RDV', danger: false }
    );
    if (!ok) return;
    try {
      await bookingApi.confirmAppointment(id);
      toast.success('RDV confirmé');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onDecline = async (id) => {
    const ok = await confirmModal('Refuser cette demande ?', {
      confirmText: 'Refuser',
      danger: true,
    });
    if (!ok) return;
    try {
      await bookingApi.declineAppointment(id);
      toast.success('Demande refusée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onMarkDone = async (appointment) => {
    const startsMs = new Date(appointment.starts_at).getTime();
    if (startsMs > Date.now()) {
      const ok = await confirmModal(
        `Ce rendez-vous est prévu le ${formatWhen(appointment.starts_at)}. Le marquer terminé maintenant ?`,
        { confirmText: 'Marquer terminé', danger: false }
      );
      if (!ok) return;
    }
    try {
      await bookingApi.markDoneAppointment(appointment.id);
      toast.success('Marqué terminé');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveHost = async () => {
    setSaving(true);
    try {
      const { host: updated } = await bookingApi.updateMyHost({
        slug: slugDraft.trim(),
        display_name: displayDraft.trim(),
      });
      setHost(updated);
      toast.success('Hôte mis à jour');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isDayActive = (weekday) => rulesDraft.some((r) => Number(r.weekday) === weekday);

  const toggleWeekday = (weekday) => {
    if (isDayActive(weekday)) {
      setRulesDraft((prev) => prev.filter((r) => Number(r.weekday) !== weekday));
      return;
    }
    setRulesDraft((prev) => [
      ...prev,
      { weekday, ...DEFAULT_RANGE },
    ]);
  };

  const addRangeForDay = (weekday) => {
    const existing = rulesForWeekday(rulesDraft, weekday);
    const last = existing[existing.length - 1];
    let nextRange = { ...DEFAULT_RANGE };
    if (last && parseMinutes(last.end_time) < parseMinutes('17:00')) {
      const start = last.end_time;
      const endMins = Math.min(parseMinutes(start) + 120, parseMinutes('18:00'));
      nextRange = {
        start_time: start,
        end_time: `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
      };
    }
    setRulesDraft((prev) => [
      ...prev.filter((r) => Number(r.weekday) !== weekday),
      ...existing.map((r) => ({ weekday, ...r })),
      { weekday, ...nextRange },
    ]);
  };

  const removeRangeForDay = (weekday, index) => {
    const existing = rulesForWeekday(rulesDraft, weekday);
    const next = existing.filter((_, i) => i !== index);
    setRulesDraft((prev) => [
      ...prev.filter((r) => Number(r.weekday) !== weekday),
      ...next.map((r) => ({ weekday, ...r })),
    ]);
  };

  const updateRangeForDay = (weekday, index, field, value) => {
    const existing = rulesForWeekday(rulesDraft, weekday);
    const next = existing.map((r, i) =>
      i === index ? { ...r, [field]: normalizeTimeValue(value) } : r
    );
    setRulesDraft((prev) => [
      ...prev.filter((r) => Number(r.weekday) !== weekday),
      ...next.map((r) => ({ weekday, ...r })),
    ]);
  };

  const saveAvailability = async () => {
    const payload = serializeRules(rulesDraft);
    const clientError = validateAvailabilityDraft(payload);
    if (clientError) {
      toast.error(clientError);
      return;
    }
    setSavingAvailability(true);
    try {
      const { availability_rules } = await bookingApi.replaceAvailabilityRules(payload);
      setRules(availability_rules);
      setRulesDraft(availability_rules);
      toast.success('Disponibilités mises à jour');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingAvailability(false);
    }
  };

  const availabilityDirty =
    JSON.stringify(serializeRules(rulesDraft)) !== JSON.stringify(serializeRules(rules));

  const publicUrlHint = host
    ? `${window.location.origin.replace(/:\d+$/, '')} → widget Hostinger · slug « ${host.slug} »`
    : '';

  return (
    <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', background: palette.bg }}>
      <div style={{ padding: '32px 40px 56px', maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${palette.primarySoft}, rgba(123,58,237,0.08))`,
              border: `1px solid ${palette.primaryBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarDays size={20} style={{ color: palette.primary }} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: palette.ink }}>
              Entretiens
            </h1>
            <div style={{ fontSize: 12, color: palette.muted, marginTop: 2 }}>
              Demandes de RDV · validation humaine obligatoire · pas un CRM
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              border: `1px solid ${palette.cardBorder}`,
              background: palette.cardBg,
              color: palette.ink,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>

        <div
          style={{
            marginTop: 16,
            marginBottom: 24,
            padding: '12px 16px',
            borderRadius: 14,
            border: `1px solid ${palette.primaryBorder}`,
            background: palette.primarySoft,
            fontSize: 13,
            color: palette.ink,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <Link2 size={16} style={{ color: palette.primary, marginTop: 2, flexShrink: 0 }} />
          <div>
            <strong>Lien public</strong> : le widget Sabiloo appelle{' '}
            <code style={{ fontSize: 12 }}>/api/booking/public/{host?.slug || '…'}/…</code>
            {publicUrlHint ? (
              <div style={{ color: palette.muted, marginTop: 4, fontSize: 12 }}>{publicUrlHint}</div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'inbox', label: 'Demandes' },
            { key: 'settings', label: 'Dispos & profil' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                border: tab === t.key ? `1px solid ${palette.primaryBorder}` : `1px solid ${palette.cardBorder}`,
                background: tab === t.key ? palette.primarySoft : palette.cardBg,
                color: tab === t.key ? palette.primary : palette.ink,
                fontWeight: tab === t.key ? 700 : 500,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: palette.muted, padding: 24 }}>Chargement…</div>
        ) : tab === 'inbox' ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['requested', 'confirmed', '', 'declined', 'done', 'cancelled'].map((s) => {
                const label = s === '' ? 'Tous' : STATUS_META[s]?.label || s;
                const active = filter === s;
                return (
                  <button
                    key={s || 'all'}
                    type="button"
                    onClick={() => setFilter(s)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: active ? `1px solid ${palette.primaryBorder}` : `1px solid ${palette.cardBorder}`,
                      background: active ? palette.primarySoft : 'transparent',
                      color: active ? palette.primary : palette.muted,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {appointments.length === 0 ? (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  borderRadius: 16,
                  border: `1px dashed ${palette.cardBorder}`,
                  color: palette.muted,
                }}
              >
                Aucune demande pour ce filtre.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {appointments.map((a) => {
                  const meta = STATUS_META[a.status] || STATUS_META.requested;
                  return (
                    <div
                      key={a.id}
                      style={{
                        padding: 18,
                        borderRadius: 16,
                        background: palette.cardBg,
                        border: `1px solid ${palette.cardBorder}`,
                        boxShadow: '0 1px 3px rgba(10,14,39,0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                color: meta.color,
                                background: meta.bg,
                                padding: '3px 8px',
                                borderRadius: 6,
                              }}
                            >
                              {meta.label}
                            </span>
                            <span style={{ fontSize: 13, color: palette.muted }}>{a.offering_title}</span>
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: palette.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <User size={16} /> {a.contact_name}
                          </div>
                          <div style={{ fontSize: 13, color: palette.muted, marginTop: 4 }}>
                            {a.contact_email}
                            {a.contact_phone ? ` · ${a.contact_phone}` : ''}
                            {a.contact_activity ? ` · ${a.contact_activity}` : ''}
                          </div>
                          <div style={{ fontSize: 13, color: palette.ink, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Clock size={14} style={{ color: palette.primary }} />
                            {formatWhen(a.starts_at)}
                          </div>
                          {a.motif ? (
                            <div style={{ fontSize: 13, color: palette.muted, marginTop: 8, fontStyle: 'italic' }}>
                              « {a.motif} »
                            </div>
                          ) : null}
                        </div>
                        {a.status === 'requested' && (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <button
                              type="button"
                              onClick={() => onConfirm(a.id)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: 'none',
                                background: palette.green,
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontSize: 13,
                              }}
                            >
                              <Check size={16} /> Confirmer
                            </button>
                            <button
                              type="button"
                              onClick={() => onDecline(a.id)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: `1px solid ${palette.red}`,
                                background: palette.redSoft,
                                color: palette.red,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontSize: 13,
                              }}
                            >
                              <X size={16} /> Refuser
                            </button>
                          </div>
                        )}
                        {a.status === 'confirmed' && (
                          <button
                            type="button"
                            onClick={() => onMarkDone(a)}
                            style={{
                              padding: '10px 14px',
                              borderRadius: 12,
                              border: `1px solid ${palette.primaryBorder}`,
                              background: palette.primarySoft,
                              color: palette.primary,
                              fontWeight: 700,
                              cursor: 'pointer',
                              fontSize: 13,
                              height: 'fit-content',
                            }}
                          >
                            Marquer terminé
                          </button>
                        )}
                      </div>
                      <RelationContextCard
                        appointment={a}
                        hostSlug={host?.slug}
                        onRelationUpdated={load}
                        offerings={offerings}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div
              style={{
                padding: 20,
                borderRadius: 16,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <h2 style={{ margin: '0 0 14px', fontSize: 16, color: palette.ink }}>Profil public</h2>
              <div style={{ display: 'grid', gap: 12, maxWidth: 420 }}>
                <label style={{ fontSize: 12, color: palette.muted }}>
                  Nom affiché
                  <input
                    value={displayDraft}
                    onChange={(e) => setDisplayDraft(e.target.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${palette.cardBorder}`,
                      background: palette.bg,
                      color: palette.ink,
                    }}
                  />
                </label>
                <label style={{ fontSize: 12, color: palette.muted }}>
                  Slug public (URL widget)
                  <input
                    value={slugDraft}
                    onChange={(e) => setSlugDraft(e.target.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${palette.cardBorder}`,
                      background: palette.bg,
                      color: palette.ink,
                    }}
                  />
                </label>
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveHost}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 12,
                    border: 'none',
                    background: palette.primary,
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: 'fit-content',
                  }}
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>

            <div
              style={{
                padding: 20,
                borderRadius: 16,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <h2 style={{ margin: '0 0 8px', fontSize: 16, color: palette.ink }}>
                Jours disponibles
              </h2>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: palette.muted }}>
                Plusieurs plages horaires par jour (timezone {host?.timezone || 'Europe/Paris'}).
                Activer un jour vide crée 09:00–17:00 par défaut.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {WEEKDAYS.map((d) => {
                  const active = isDayActive(d.v);
                  const dayRanges = rulesForWeekday(rulesDraft, d.v);
                  return (
                    <div
                      key={d.v}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: `1px solid ${active ? palette.primaryBorder : palette.cardBorder}`,
                        background: active ? palette.primarySoft : 'transparent',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          marginBottom: active ? 10 : 0,
                        }}
                      >
                        <div style={{ fontWeight: 700, color: active ? palette.primary : palette.muted }}>
                          {d.full}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleWeekday(d.v)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 999,
                            border: `1px solid ${active ? palette.primaryBorder : palette.cardBorder}`,
                            background: active ? palette.cardBg : 'transparent',
                            color: active ? palette.primary : palette.muted,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {active ? 'Désactiver' : 'Activer'}
                        </button>
                      </div>
                      {active ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {dayRanges.map((range, index) => (
                            <div
                              key={`${d.v}-${index}`}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                            >
                              <input
                                type="time"
                                value={range.start_time}
                                onChange={(e) =>
                                  updateRangeForDay(d.v, index, 'start_time', e.target.value)
                                }
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: 8,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: palette.cardBg,
                                  color: palette.ink,
                                  fontSize: 13,
                                }}
                              />
                              <span style={{ color: palette.muted, fontSize: 13 }}>→</span>
                              <input
                                type="time"
                                value={range.end_time}
                                onChange={(e) =>
                                  updateRangeForDay(d.v, index, 'end_time', e.target.value)
                                }
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: 8,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: palette.cardBg,
                                  color: palette.ink,
                                  fontSize: 13,
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeRangeForDay(d.v, index)}
                                title="Supprimer cette plage"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: 'transparent',
                                  color: palette.muted,
                                  cursor: 'pointer',
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addRangeForDay(d.v)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              alignSelf: 'flex-start',
                              padding: '6px 10px',
                              borderRadius: 8,
                              border: `1px dashed ${palette.primaryBorder}`,
                              background: 'transparent',
                              color: palette.primary,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Plus size={14} />
                            Ajouter une plage
                          </button>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: palette.muted }}>Jour indisponible</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  onClick={saveAvailability}
                  disabled={savingAvailability || !availabilityDirty}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: availabilityDirty ? palette.primary : palette.cardBorder,
                    color: availabilityDirty ? '#fff' : palette.muted,
                    fontWeight: 700,
                    cursor: savingAvailability || !availabilityDirty ? 'not-allowed' : 'pointer',
                    opacity: savingAvailability ? 0.7 : 1,
                  }}
                >
                  {savingAvailability ? 'Enregistrement…' : 'Enregistrer les disponibilités'}
                </button>
                {availabilityDirty ? (
                  <span style={{ fontSize: 12, color: palette.orange }}>Modifications non enregistrées</span>
                ) : null}
              </div>
            </div>

            <div
              style={{
                padding: 20,
                borderRadius: 16,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <h2 style={{ margin: '0 0 12px', fontSize: 16, color: palette.ink }}>
                Types d&apos;entretien
              </h2>
              {offerings.map((o) => (
                <div
                  key={o.id}
                  style={{
                    padding: '12px 0',
                    borderTop: `1px solid ${palette.cardBorder}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: palette.ink }}>{o.title}</div>
                    <div style={{ fontSize: 12, color: palette.muted }}>
                      code <code>{o.code}</code> · {o.duration_min} min
                      {o.is_active ? '' : ' · inactif'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
